import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarX, Compass, Plane, Bus, Hotel, Car, Mountain } from "lucide-react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import Shell from "@/components/Shell";

const ICONS = { flight: Plane, bus: Bus, hotel: Hotel, cab: Car, experience: Mountain };
const STATUS_STYLE = {
  confirmed: "bg-secondary text-primary",
  pending: "bg-secondary text-muted-foreground",
  completed: "bg-primary text-primary-foreground",
  cancelled: "bg-destructive/10 text-destructive",
};

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Shell>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-display text-5xl tracking-tight text-foreground">My trips</h1>
        <p className="text-muted-foreground mt-3">Every experience, flight, stay and ride you've booked.</p>

        {loading ? (
          <div className="mt-10 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-secondary/60 animate-pulse" />)}</div>
        ) : bookings.length === 0 ? (
          <div className="mt-16 text-center" data-testid="no-bookings">
            <CalendarX className="mx-auto text-muted-foreground" size={44} />
            <p className="mt-4 text-lg text-foreground">No trips yet</p>
            <Link to="/explore" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold mt-6"><Compass size={16} /> Start exploring</Link>
          </div>
        ) : (
          <div className="mt-10 space-y-4" data-testid="bookings-list">
            {bookings.map((b, i) => {
              const Icon = ICONS[b.type] || Mountain;
              return (
                <motion.div key={b.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl border border-primary/10 bg-card p-5 flex items-center justify-between gap-4" data-testid={`booking-${i}`}>
                  <div className="flex items-center gap-4">
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-secondary text-accent"><Icon size={20} /></span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{b.title}</h3>
                        <span className={`text-xs font-semibold uppercase px-2.5 py-0.5 rounded-full ${STATUS_STYLE[b.status] || "bg-secondary"}`}>{b.status}</span>
                      </div>
                      <p className="text-xs text-muted-foreground capitalize mt-0.5">{b.type} · {b.payment_status}</p>
                    </div>
                  </div>
                  <span className="font-display text-2xl text-foreground">{inr(b.amount)}</span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}
